import type { CustomerOrderingEligibility } from "@shared-core/data/customer-api-types";
import { Card } from "../../app/components/primitives/card";
import { ShieldAlert } from "lucide-react";

// The entry-point states (brief §8 J3, §9d): a card-only customer, a person
// without the ORDERING capability, a credit-stop account, and a customer whose
// trading company cannot take new orders yet (trading companies D2). All but
// card-only are refusals, rendered in place of the ordering surface itself — "not a
// UI-only restriction" (brief §6) means the page never even shows the form,
// rather than showing it and refusing on submit. Card-only is not a refusal:
// card ordering is basic and "on account" is a staff-granted extra, so a card-only
// person sees the page with `CardOnlyNotice` above it.

type BlockedReason = NonNullable<CustomerOrderingEligibility["reason"]>;

const COPY: Record<BlockedReason, { heading: string; body: string }> = {
  card_only: {
    heading: "You can order and pay by card",
    body: "Ordering on account isn't set up for you. To order on your company's account, ask your Pollards contact to switch it on, or call us on 01752 663111.",
  },
  no_capability: {
    heading: "You don't have access to place orders",
    body: "Ordering in the portal isn't switched on for you. Please contact your Pollards contact to place this order.",
  },
  credit_stop: {
    heading: "We can't take new orders on this account right now",
    body: "Your account is on hold. Please contact your account manager to sort this out before we can take further orders.",
  },
  // Provisional wording (trading companies D2, for the programme's wording review). Names no company: the customer
  // is told where to go, as the 409 from placing an order says.
  trading_company_not_trading: {
    heading: "We can't take new orders on this account online just now",
    body: "Please contact your account manager to place this order.",
  },
};

/** The reason ordering is refused outright, or null when the page should show (eligible, or card-only). */
export function refusalReason(
  eligibility: CustomerOrderingEligibility | null,
): BlockedReason | null {
  if (!eligibility || eligibility.eligible) return null;
  const reason = eligibility.reason ?? "no_capability";
  return reason === "card_only" ? null : reason;
}

/** True when the person can order, by card, but is not set up to order on account. */
export function isCardOnly(
  eligibility: CustomerOrderingEligibility | null,
): boolean {
  return (
    eligibility !== null &&
    !eligibility.eligible &&
    eligibility.reason === "card_only"
  );
}

/** The card-only state as a notice above the page, not in place of it. */
export function CardOnlyNotice() {
  const copy = COPY.card_only;
  return (
    <div
      role="note"
      className="flex items-start gap-3 rounded-md border bg-muted/30 p-3 text-sm"
    >
      <ShieldAlert className="mt-0.5 size-4 shrink-0 text-amber-700 dark:text-amber-400" />
      <div>
        <p className="font-medium">{copy.heading}</p>
        <p className="text-muted-foreground">{copy.body}</p>
      </div>
    </div>
  );
}

export function OrderingBlockedNotice({ reason }: { reason: BlockedReason }) {
  const copy = COPY[reason];
  return (
    <Card className="flex flex-col items-center gap-2 p-8 text-center">
      <ShieldAlert className="size-6 text-amber-700 dark:text-amber-400" />
      <p className="font-medium">{copy.heading}</p>
      <p className="max-w-sm text-sm text-muted-foreground">{copy.body}</p>
    </Card>
  );
}
